const API = process.env.REACT_APP_BACKEND_URL;
export const LANGS = { tr: "Türkçe", en: "English", de: "Deutsch", fr: "Français" };
export const LOCALES = { tr: "tr_TR", en: "en_US", de: "de_DE", fr: "fr_FR" };
const PREFIX_RE = /^\/(en|de|fr)(?=\/|$)/;
const SKIP = new Set(["SCRIPT", "STYLE", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT", "NOSCRIPT", "SVG", "svg"]);
const ATTRS = ["placeholder", "title", "aria-label"];
const META_SEL = ['meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:description"]', 'meta[name="twitter:title"]', 'meta[name="twitter:description"]'];
const LETTERS = /[A-Za-zÇĞİÖŞÜçğıöşü]/;

const urlMatch = window.location.pathname.match(PREFIX_RE);
export const LANG = urlMatch ? urlMatch[1] : "tr";
export const LANG_PREFIX = urlMatch ? `/${LANG}` : "";
export const METHODS_PATHS = { tr: "/yontemler", en: "/make-money", de: "/geld-verdienen", fr: "/gagner-de-l-argent" };
export const METHODS_PATH = METHODS_PATHS[LANG];
export const methodHref = (m) => `${METHODS_PATH}/${(m.slugs && m.slugs[LANG]) || m.slug}`;

export function detectLang() { return LANG; }
export function stripLang(pathname) { return pathname.replace(PREFIX_RE, "") || "/"; }
export function langPath(path, lang = LANG) {
  if (lang === "tr") return path;
  return path === "/" ? `/${lang}` : `/${lang}${path}`;
}

export function setLang(lang) {
  localStorage.setItem("fa-lang", lang);
  const { pathname, search, hash } = window.location;
  window.location.href = langPath(stripLang(pathname), lang) + search + hash;
}

export function suggestedLang() {
  if (localStorage.getItem("fa-lang") || sessionStorage.getItem("fa-lang-suggest-dismissed")) return null;
  const nav = (navigator.language || "tr").slice(0, 2).toLowerCase();
  return LANGS[nav] && nav !== LANG ? nav : null;
}
export function dismissLangSuggest() { sessionStorage.setItem("fa-lang-suggest-dismissed", "1"); }

const dict = new Map();
const pending = new Set();
const originals = new WeakMap();
const metaOriginals = new Map();
let timer = null, inflight = false, titleOriginal;
let resolveReady;
export const dictReady = new Promise((r) => { resolveReady = r; });

function ok(t) { const s = t.trim(); return s.length > 1 && s.length <= 1200 && LETTERS.test(s) && !/^[\w.+-]+@[\w-]+\.[\w.]+$|^https?:\/\//.test(s); }

export function t(text) {
  if (LANG === "tr" || typeof text !== "string") return text;
  const tr = dict.get(text.trim());
  if (tr) return tr;
  if (ok(text)) { pending.add(text.trim()); schedule(); }
  return text;
}

function apply(node, original) {
  const tr = dict.get(original.trim());
  if (!tr) return false;
  const lead = original.match(/^\s*/)[0], tail = original.match(/\s*$/)[0];
  if (node.nodeType === 3) { if (node.nodeValue !== lead + tr + tail) node.nodeValue = lead + tr + tail; }
  return true;
}

function visitText(node) {
  if (!node.parentElement || SKIP.has(node.parentElement.tagName) || node.parentElement.closest("[data-no-translate],[translate=no]")) return;
  let original = originals.get(node);
  if (original === undefined || (node.nodeValue !== (original.match(/^\s*/)[0] + (dict.get(original.trim()) || "") + original.match(/\s*$/)[0]) && node.nodeValue !== original)) {
    original = node.nodeValue; originals.set(node, original);
  }
  if (!ok(original)) return;
  if (!apply(node, original)) pending.add(original.trim());
}

function visitAttrs(el) {
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (!v || !ok(v)) continue;
    const key = `attr:${a}`;
    const store = originals.get(el) || {};
    if (store[key] === undefined || (v !== store[key] && v !== dict.get(store[key].trim()))) { store[key] = v; originals.set(el, store); }
    const tr = dict.get(store[key].trim());
    if (tr) { if (v !== tr) el.setAttribute(a, tr); } else pending.add(store[key].trim());
  }
}

function visitTitle() {
  const cur = document.title;
  if (titleOriginal === undefined || (cur !== titleOriginal && cur !== dict.get(titleOriginal.trim()))) titleOriginal = cur;
  const tr = dict.get(titleOriginal.trim());
  if (tr) { if (cur !== tr) document.title = tr; } else if (ok(titleOriginal)) pending.add(titleOriginal.trim());
}

function visitMetas() {
  for (const sel of META_SEL) {
    const el = document.head.querySelector(sel);
    if (!el) continue;
    const v = el.getAttribute("content") || "";
    let o = metaOriginals.get(sel);
    if (o === undefined || (v !== o && v !== dict.get(o.trim()))) { o = v; metaOriginals.set(sel, o); }
    if (!ok(o)) continue;
    const tr = dict.get(o.trim());
    if (tr) { if (v !== tr) el.setAttribute("content", tr); } else pending.add(o.trim());
  }
}

function walk(root) {
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, { acceptNode: (n) => (n.nodeType === 1 && SKIP.has(n.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT) });
  let n = tw.currentNode;
  while (n) { if (n.nodeType === 3) visitText(n); else if (n.nodeType === 1) visitAttrs(n); n = tw.nextNode(); }
  document.title && visitTitle();
  visitMetas();
  flush();
}

async function flush() {
  if (inflight || pending.size === 0) return;
  inflight = true;
  const texts = [...pending].slice(0, 200);
  texts.forEach((t) => pending.delete(t));
  try {
    const r = await fetch(`${API}/api/i18n/translate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lang: LANG, texts }) });
    const data = await r.json();
    Object.entries(data).forEach(([k, v]) => dict.set(k, v));
  } catch { /* offline: keep Turkish */ }
  inflight = false;
  schedule();
}

function schedule() { clearTimeout(timer); timer = setTimeout(() => walk(document.getElementById("root") || document.body), 120); }

const BOT_RE = /bot|crawl|spider|slurp|headless|lighthouse|prerender|facebookexternalhit|preview/i;
const isBot = () => BOT_RE.test(navigator.userAgent || "") || navigator.webdriver === true;

export async function startTranslator() {
  const saved = localStorage.getItem("fa-lang");
  if (!urlMatch && saved && LANGS[saved] && saved !== "tr" && window.location.pathname === "/" && !isBot()) {
    const { search, hash } = window.location;
    window.location.replace(langPath("/", saved) + search + hash);
    return;
  }
  if (urlMatch) localStorage.setItem("fa-lang", LANG);
  document.documentElement.lang = LANG;
  if (LANG === "tr") { resolveReady(); return; }
  try { const r = await fetch(`${API}/api/i18n/${LANG}`); Object.entries(await r.json()).forEach(([k, v]) => dict.set(k, v)); } catch { /* ignore */ }
  resolveReady();
  walk(document.getElementById("root") || document.body);
  new MutationObserver(schedule).observe(document.body, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ATTRS });
  new MutationObserver(schedule).observe(document.head, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["content"] });
}
